import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { AlertSingleLine, DataCard, Loader, SdiProfile, styles } from '@sdir/sds';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { formatFullName } from '@sdir/blueprint.aps/lib/helpers/formattingHelpers';
import { CardRow, Card } from '../../Atoms';
import { ContactInfoSummaryCard } from '../../Molecules';
import { InfoPageLayout } from '../../Organisms';
import { useCurrentPerson } from '../../../services/hooks/useCurrentPerson';
import { useCurrentApplication } from '../../../services/hooks/useCurrentApplication';
import { ToggleProfile, ToggleShareCompetence } from '../../../constants/permissions';
import { useFeatureToggles } from '../../../services/hooks';
import { ShareCompetence } from '../../Organisms/ShareCompetence';

const ProfilePage: React.FC = () => {
  const intl = useIntl();
  const featureToggle = useFeatureToggles();
  const {
    person: { loading, result: personalInfo },
  } = useCurrentPerson();

  const { application, refresh } = useCurrentApplication();

  const { name, personalNr } = useMemo(() => {
    const personName = formatFullName(
      personalInfo?.firstName || '',
      personalInfo?.middleName || '',
      personalInfo?.lastName || ''
    );
    return { name: personName, personalNr: personalInfo?.personalIdentificationNumber || '' };
  }, [personalInfo]);

  const showProfile = !isToggleOn(featureToggle?.result, ToggleProfile, personalNr);
  const showShare = !isToggleOn(featureToggle?.result, ToggleShareCompetence, personalNr);

  useEffect(() => {
    refresh?.();
  }, []);

  if (!showProfile) {
    return (
      <AlertSingleLine
        title="Error"
        type="error"
        text={intl.formatMessage({ id: 'utils.errors.insufficientaccess' })}
      />
    );
  }

  return (
    <InfoPageLayout
      title={
        name
          ? `${intl.formatMessage({ id: 'profile.title' })} - ${name}`
          : intl.formatMessage({ id: 'profile.title' })
      }
      description={intl.formatMessage({ id: 'profile.description' })}
      icon={<SdiProfile color={styles.colors.secondary.seaBlue4} size="l" />}
    >
      {loading && <Loader />}
      {!loading && (
        <DataCard title={intl.formatMessage({ id: 'profile.personalinfo' })}>
          <InfoContainer>
            <ContactInfoWrapper>
              <ContactInfoSummaryCard />
              <Card heading={intl.formatMessage({ id: 'profile.otherinfo' })}>
                <CardRow label={intl.formatMessage({ id: 'profile.personalidentitynumber' })}>
                  {personalInfo?.personalIdentificationNumber || '-'}
                </CardRow>
                <CardRow label={intl.formatMessage({ id: 'profile.nationality' })}>
                  {personalInfo?.nationality || '-'}
                </CardRow>
              </Card>
            </ContactInfoWrapper>
          </InfoContainer>
        </DataCard>
      )}
      {!loading && showShare && (
        <DataCard title={intl.formatMessage({ id: 'profile.shared.header' })}>
          <SharingWrapper>
            <ShareCompetence personalNr={personalNr} />
          </SharingWrapper>
        </DataCard>
      )}
    </InfoPageLayout>
  );
};

const InfoContainer = styled.div`
  display: flex;
  justify-content: space-between;

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    flex-direction: column;
  }
`;

const ContactInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;

  & > div {
    box-shadow: none;
  }
`;

const SharingWrapper = styled.div`
  flex: 1;

  & > div {
    box-shadow: none;
  }
`;

export default ProfilePage;
